import { createContext, useContext, useEffect, useState, useRef } from 'react';
import type { ReactNode } from 'react';
import { supabase } from '../lib/supabase';
import type { User, Session } from '@supabase/supabase-js';

interface UserProfile {
  id: string;
  nome: string;
  email: string;
  perfil_id: string;
  foto_url?: string;
  cpf?: string;
  telefone?: string;
  data_nascimento?: string;
  preferencias?: {
    pageSize?: number;
    [key: string]: any;
  };
  perfil?: {
    id: string;
    nome: string;
    tempo_sessao_minutos?: number;
  } | null;
}

interface AlunoProfile {
  id: string;
  user_id: string;
  turma_id?: string | null;
  codigo_matricula?: string | null;
  ativo?: boolean;
  data_nascimento?: string | null;
  turma_serie_nome?: string | null; // Apenas a string final é armazenada no perfil
  nome?: string | null;
  foto_url?: string | null;
}

// NEW: Definindo as interfaces para a estrutura de dados aninhada *conforme inferido pelo TypeScript no erro*
interface SupabaseFetchedSerie {
  nome: string;
}

interface SupabaseFetchedTurma {
  serie: SupabaseFetchedSerie[]; // TypeScript infere 'serie' como um array
}

interface SupabaseFetchedAlunoData {
  id: string;
  user_id: string;
  turma_id: string | null;
  codigo_matricula: string | null;
  ativo: boolean;
  data_nascimento: string | null;
  nome?: string | null;
  foto_url?: string | null;
  turma: any; // We use dynamic any mapping below
}


interface AuthContextType {
  user: User | null;
  session: Session | null;
  userProfile: UserProfile | null;
  alunoProfile: AlunoProfile | null;
  guardianStudents: (AlunoProfile & { nome?: string })[];
  switchStudent: (alunoId: string) => void;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signOut: () => Promise<void>;
  hasPermission: (recursoRota: string, acao?: 'visualizar' | 'criar' | 'editar' | 'apagar' | 'apagar_proprio') => boolean;
  reloadUserProfile: () => Promise<void>;
  isRestricted: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [alunoProfile, setAlunoProfile] = useState<AlunoProfile | null>(null);
  const [guardianStudents, setGuardianStudents] = useState<(AlunoProfile & { nome?: string })[]>([]);
  const [userPermissions, setUserPermissions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const profileLoadedRef = useRef(false); // NEW: Track profile load state for use in closures

  // Single Session Control
  const [currentBrowserSessionId, setCurrentBrowserSessionId] = useState<string | null>(null);

  const loadUserProfile = async (userId: string) => {
    try {
      const { data: userData, error: userError } = await supabase
        .from('usuarios')
        .select('id, nome, email, perfil_id, foto_url, cpf, telefone, data_nascimento, preferencias')
        .eq('auth_id', userId)
        .maybeSingle();

      if (userError && userError.code !== 'PGRST116') throw userError;
      if (!userData) {
        setUserProfile(null);
        setAlunoProfile(null);
        setUserPermissions([]);
        return;
      }

      let userProfileWithPerfil: UserProfile = { ...userData, perfil: null };

      if (userData.perfil_id) {
        const { data: perfilData, error: perfilError } = await supabase
          .from('perfis_acesso')
          .select('id, nome, tempo_sessao_minutos')
          .eq('id', userData.perfil_id)
          .single();
        if (!perfilError) {
          userProfileWithPerfil.perfil = perfilData;
        } else {
          console.error('Erro ao buscar perfil:', perfilError);
        }
      }

      setUserProfile(userProfileWithPerfil);
      profileLoadedRef.current = true; // NEW: Mark as loaded

      if (userProfileWithPerfil?.perfil?.nome?.toLowerCase() === 'aluno') {
        // ... existing student fetching logic ...
        const { data: alunoData, error: alunoError } = await supabase
          .from('alunos')
          .select('id, user_id, turma_id, codigo_matricula, ativo, data_nascimento, nome, foto_url, turma:turmas(nome, serie:series(nome))')
          .eq('user_id', userProfileWithPerfil.id)
          .single();

        if (alunoError && alunoError.code !== 'PGRST116') {
          console.error('Error fetching alunoProfile:', alunoError);
          throw alunoError;
        }

        if (alunoData) {
          const typedAlunoData = alunoData as SupabaseFetchedAlunoData;
          let turmaSerieStr = '';
          if (typedAlunoData.turma?.serie?.nome) turmaSerieStr += typedAlunoData.turma.serie.nome;
          if (typedAlunoData.turma?.nome) turmaSerieStr += (turmaSerieStr ? ' - ' : '') + typedAlunoData.turma.nome;

          setAlunoProfile({
            id: typedAlunoData.id,
            user_id: typedAlunoData.user_id,
            turma_id: typedAlunoData.turma_id,
            codigo_matricula: typedAlunoData.codigo_matricula,
            ativo: typedAlunoData.ativo,
            data_nascimento: typedAlunoData.data_nascimento,
            turma_serie_nome: turmaSerieStr || null,
            nome: typedAlunoData.nome,
            foto_url: typedAlunoData.foto_url,
          });
        } else {
          setAlunoProfile(null);
        }
      } else if (userProfileWithPerfil?.perfil?.nome?.toLowerCase() === 'responsável' || userProfileWithPerfil?.perfil?.nome?.toLowerCase() === 'responsavel') {
        const cpfLimpo = userProfileWithPerfil.cpf?.replace(/\\D/g, '');
        if (cpfLimpo) {
          const { data: responsaveisData, error: respError } = await supabase
            .from('responsaveis')
            .select('aluno_id')
            .eq('cpf', cpfLimpo);
            
          if (!respError && responsaveisData && responsaveisData.length > 0) {
            const alunoIds = responsaveisData.map(r => r.aluno_id);
            const { data: alunosData, error: alunosError } = await supabase
              .from('alunos')
              .select('id, user_id, turma_id, codigo_matricula, ativo, data_nascimento, nome, foto_url, turma:turmas(nome, serie:series(nome))')
              .in('id', alunoIds);
              
            if (!alunosError && alunosData) {
              const mappedStudents = alunosData.map(a => {
                let turmaSerieStr = '';
                if (a.turma?.serie?.nome) turmaSerieStr += a.turma.serie.nome;
                if (a.turma?.nome) turmaSerieStr += (turmaSerieStr ? ' - ' : '') + a.turma.nome;
                
                return {
                  id: a.id,
                  user_id: a.user_id,
                  turma_id: a.turma_id,
                  codigo_matricula: a.codigo_matricula,
                  ativo: a.ativo,
                  data_nascimento: a.data_nascimento,
                  turma_serie_nome: turmaSerieStr || null,
                  nome: a.nome,
                  foto_url: a.foto_url
                };
              });
              
              setGuardianStudents(mappedStudents);
              
              const savedStudentId = localStorage.getItem('antares_selected_student_id');
              const defaultStudent = mappedStudents.find(s => s.id === savedStudentId) || mappedStudents[0];
              
              if (defaultStudent) {
                setAlunoProfile(defaultStudent);
              }
            } else {
              setAlunoProfile(null);
              setGuardianStudents([]);
            }
          } else {
            setAlunoProfile(null);
            setGuardianStudents([]);
          }
        } else {
          setAlunoProfile(null);
          setGuardianStudents([]);
        }
      } else {
        setAlunoProfile(null);
        setGuardianStudents([]);
      }

      if (userProfileWithPerfil?.perfil_id) {
        const { data: permissoesData, error: permError } = await supabase
          .from('permissoes')
          .select('*, recurso:recursos_sistema(*)')
          .eq('perfil_id', userProfileWithPerfil.perfil_id);
        if (!permError) {
          setUserPermissions(permissoesData || []);
        } else {
          console.error('Erro ao buscar permissões:', permError);
        }
      }

    } catch (error) {
      console.error('Erro ao carregar perfil do usuário:', error);
      setUserPermissions([]);
    } finally {
      setLoading(false);
    }
  };

  const reloadUserProfile = async () => {
    if (user) {
      await loadUserProfile(user.id);
    }
  };

  // Logic to enforce single session
  useEffect(() => {
    if (!user) return;

    // Generate a unique ID for THIS browser session instance
    // Using a simple random string/timestamp combo if not using a UUID lib, 
    // or just crypto.randomUUID if available (modern browsers)
    const newSessionId = self.crypto.randomUUID();
    setCurrentBrowserSessionId(newSessionId);

    // 1. Update the database with this new session ID
    const registerSession = async () => {
      try {
        console.log('[Auth] Tentando registrar sessão:', newSessionId);
        const { data, error } = await supabase
          .from('usuarios')
          .update({ current_session_id: newSessionId })
          .eq('auth_id', user.id)
          .select();

        if (error) {
          console.error('[Auth] Erro ao registrar sessão no DB:', error);
        } else {
          console.log('[Auth] Sessão registrada. Rows updated:', data?.length);
          if (data?.length === 0) {
            console.warn('[Auth] ALERTA: Nenhuma linha foi atualizada! Verifique se a tabela "usuarios" tem um registro com auth_id =', user.id);
          }
        }
      } catch (err) {
        console.error('[Auth] Exceção ao registrar sessão:', err);
      }
    };
    registerSession();

    // 2. Subscribe to changes on the 'usuarios' table for this user
    console.log('[Auth] Iniciando subscription para usuario:', user.id);
    const channel = supabase
      .channel(`session_control_${user.id}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'usuarios',
          filter: `auth_id=eq.${user.id}`,
        },
        (payload) => {
          console.log('[Auth] Evento de update recebido:', payload);
          const remoteSessionId = payload.new.current_session_id;
          // If the DB session ID changes and it's NOT the one we just set, logout
          if (remoteSessionId && remoteSessionId !== newSessionId) {
            console.warn('[Auth] Sessão invalidada por outro dispositivo. ID Remoto:', remoteSessionId, 'ID Local:', newSessionId);
            alert('Sua sessão foi encerrada pois você conectou em outro dispositivo/aba.');
            signOut();
          }
        }
      )
      .subscribe((status, err) => {
        console.log('[Auth] Status da subscription:', status, err ? err : '');
      });

    return () => {
      console.log('[Auth] Limpando canal de sessão');
      supabase.removeChannel(channel);
    };
  }, [user?.id]); // Re-run only if user ID changes (login event)


  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        loadUserProfile(session.user.id);
      } else {
        setLoading(false);
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        // Silent refresh: Only set global loading to true if we don't have a profile yet.
        // We use profileLoadedRef because userProfile from the enclosure may be stale (null).
        if (!profileLoadedRef.current) {
          setLoading(true);
        }
        loadUserProfile(session.user.id);
      } else {
        setUserProfile(null);
        setAlunoProfile(null);
        setGuardianStudents([]);
        setUserPermissions([]);
        profileLoadedRef.current = false; // Reset on logout
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    return await supabase.auth.signInWithPassword({ email, password });
  };

  const signOut = async () => {
    try {
      await supabase.auth.signOut();

      // Manually clear any residual state
      setUser(null);
      setSession(null);
      setUserProfile(null);
      setAlunoProfile(null);
      setGuardianStudents([]);
      setUserPermissions([]);
      setCurrentBrowserSessionId(null);

      // Force clear Supabase tokens from LocalStorage
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith('sb-') || key.includes('supabase')) {
          localStorage.removeItem(key);
        }
      });

      // Force loading state to false to ensure UI updates
      setLoading(false);

      window.REACT_APP_NAVIGATE('/');
    } catch (error) {
      console.error('Error signing out:', error);
      // Fallback navigation even if error
      window.REACT_APP_NAVIGATE('/');
    }
  };

  const hasPermission = (recursoRota: string, acao: 'visualizar' | 'criar' | 'editar' | 'apagar' | 'apagar_proprio' = 'visualizar'): boolean => {
    if (loading || !user) {
      return false;
    }
    if (userProfile?.perfil?.nome?.toLowerCase() === 'administrador') {
      return true;
    }
    if (!userPermissions?.length) {
      return false;
    }

    const permissao = userPermissions.find((p: any) => p.recurso?.rota === recursoRota);
    if (!permissao) {
      return false;
    }

    const actionMap = {
      visualizar: 'pode_visualizar',
      criar: 'pode_criar',
      editar: 'pode_editar',
      apagar: 'pode_apagar',
      apagar_proprio: 'pode_apagar_proprio',
    };

    const result = permissao[actionMap[acao]] === true;
    return result;
  };

  const switchStudent = (alunoId: string) => {
    const student = guardianStudents.find(s => s.id === alunoId);
    if (student) {
      setAlunoProfile(student);
      localStorage.setItem('antares_selected_student_id', alunoId);
    }
  };

  const value = {
    user,
    session,
    userProfile,
    alunoProfile,
    guardianStudents,
    switchStudent,
    loading,
    signIn,
    signOut,
    hasPermission,
    reloadUserProfile,
    isRestricted: (userProfile?.perfil?.nome?.toLowerCase() === 'aluno' ||
      userProfile?.perfil?.nome?.toLowerCase() === 'responsável' ||
      userProfile?.perfil?.nome?.toLowerCase() === 'responsavel'),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  return context;
}